/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, March 30th 2022, 2:53:06 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useAPIFetchItem, useAPIMutation, useAPIFetchList, APISERVICEPATHS, useAPIForm, useAPIRequest } from "lib/api";
import {
  BillingConfiguration,
  BillingConfigurationValidationSchema,
  PaymentMethod,
  PaymentAPICredentials,
  Organization,
  NumberingSequence,
  ProcessDefinition,
  IntegrationCredentialStore,
} from "lib/schemas";
import { useGlobalScope } from "core/providers";
import { PrimaryKeyValue } from "lib/types";
import { convertTextToInitials } from "lib/utils";

/* BillingConfiguration API */

const billingConfigurationAPIServicePath = APISERVICEPATHS.billingConfigurations;

export const useAPIFetchBillingConfiguration = () =>
  useAPIFetchItem<BillingConfiguration>({
    apiServicePath: billingConfigurationAPIServicePath,
    makeRequestOnInitialMount: false,
  });

export const useAPIListBillingConfigurations = (processDefinitionId: ProcessDefinition["id"]) =>
  useAPIFetchList<BillingConfiguration>({
    apiServicePath: billingConfigurationAPIServicePath,
    apiQueryParams: { process_definition: processDefinitionId },
    makeRequestOnInitialMount: true,
  });

export const useAPIMutateBillingConfiguration = () =>
  useAPIMutation<BillingConfiguration>({
    apiServicePath: billingConfigurationAPIServicePath,
  });

const getDefaultBillingConfiguration = (
  organization: Organization,
  objectName: string,
  defaultInvoiceSequence: PrimaryKeyValue<NumberingSequence> & number,
  optionalProperties?: Partial<BillingConfiguration>
): BillingConfiguration => {
  const nameInitials = convertTextToInitials(objectName).join("");
  return {
    artifact_org: organization.id,
    currency: organization.default_currency,
    invoice_sequence: defaultInvoiceSequence,
    name: `Billing Product for ${objectName}`,
    code: `${nameInitials}`,
    due_days: 30,
    payment_methods: [],
    ...optionalProperties,
  };
};

export const useBillingConfigurationAPIForm = (billingConfigurationId: PrimaryKeyValue<BillingConfiguration>) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const newBillingConfigurationDefault = getDefaultBillingConfiguration(selectedOrganization, "", null);

  return useAPIForm({
    apiServicePath: billingConfigurationAPIServicePath,
    apiFormDataValidationSchema: BillingConfigurationValidationSchema,
    apiFormDefaultValues: newBillingConfigurationDefault,
    apiInitialFetchId: billingConfigurationId,
    primaryKey: "id",
  });
};

/* Payment Methods API */

const paymentMethodAPIServicePath = APISERVICEPATHS.paymentMethods;

export const useAPIListPaymentMethods = () =>
  useAPIFetchList<PaymentMethod>({
    apiServicePath: paymentMethodAPIServicePath,
    makeRequestOnInitialMount: false,
  });

const billingConfigurationPaymentCredentialsAPIServicePath = APISERVICEPATHS.billingConfigurationPaymentCredentials;

export const useAPIFetchBillingConfigurationPaymentCredentials = () => {
  const { makeRequest, ...rest } = useAPIRequest<PaymentAPICredentials>({
    apiServicePath: billingConfigurationPaymentCredentialsAPIServicePath,
    makeRequestOnInitialMount: false,
  });

  const fetchPaymentCredentials = React.useCallback(
    (
      configurationId: BillingConfiguration["id"],
      provider: PaymentMethod["type"],
      onFetch: (paymentCredentials: PaymentAPICredentials) => void
    ) => {
      makeRequest({ apiPathParams: [configurationId, provider], setValidAPIResponse: onFetch });
    },
    [makeRequest]
  );

  return { fetchPaymentCredentials, ...rest };
};

export const useAPIMutateBillingConfigurationPaymentCredentials = () => {
  const { deleteData, patchData, postData, putData, ...rest } = useAPIMutation<PaymentAPICredentials>({
    apiServicePath: billingConfigurationPaymentCredentialsAPIServicePath,
  });

  const removePaymentCredentials = React.useCallback(
    (
      billingConfigurationId: BillingConfiguration["id"],
      provider: PaymentMethod["type"],
      onRemoveCredentials: (credentials: PaymentAPICredentials) => void
    ) => {
      deleteData({
        apiPathParams: [billingConfigurationId, provider],
        setValidAPIResponse: () => onRemoveCredentials(null),
      });
    },
    [deleteData]
  );

  const updatePaymentCredentials = React.useCallback(
    (
      billingConfigurationId: BillingConfiguration["id"],
      provider: PaymentMethod["type"],
      credentialStoreId: IntegrationCredentialStore["id"],
      onUpdateCredentials: (credentials: PaymentAPICredentials) => void
    ) => {
      patchData({
        apiPathParams: [billingConfigurationId, provider],
        body: JSON.stringify({ credential_store_id: credentialStoreId }),
        setValidAPIResponse: onUpdateCredentials,
      });
    },
    [patchData]
  );

  return { removePaymentCredentials, updatePaymentCredentials, ...rest };
};
